/**
 * AUTO-BOT Panel
 *
 * Manages automated trading bots - displays list of bots with controls
 * for enabling/disabling, pausing/resuming, and viewing status.
 */

import { CONFIG } from '../config';
import React, { useEffect, useState, useMemo } from 'react';
import {
  useAutoBotStore,
  useAutoBotGlobalEnabled,
  useAutoBotActiveBots,
  useAutoBotTotalBots,
  useExecutions,
  useBotExecutions,
  initializeAutoBotStore,
} from '../store/autoBotStore';
import {
  X,
  Bot,
  Power,
  Play,
  Pause,
  Trash2,
  Settings,
  Plus,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  Zap,
  TrendingUp,
  Activity,
  History,
  ChevronDown,
  ChevronUp,
  ArrowUpCircle,
  ArrowDownCircle,
  FileText,
  Circle,
} from 'lucide-react';
import type { AutoBot, BotStatus, AutoBotExecution, DistributiveOmit } from '@shared/types/autoBot';
import { isBreakOfStructureBot } from '@shared/types/autoBot';
import AutoBotEditor from './AutoBotEditor';
import './AutoBotPanel.css';

// ============================================================================
// STATUS HELPERS
// ============================================================================

const getStatusColor = (status: BotStatus): string => {
  switch (status) {
    case 'active':
      return 'var(--success-color, #22c55e)';
    case 'paused':
      return 'var(--warning-color, #f59e0b)';
    case 'circuit_breaker':
      return 'var(--error-color, #ef4444)';
    case 'stopped':
    default:
      return 'var(--muted-color, #6b7280)';
  }
};

const getStatusIcon = (status: BotStatus) => {
  switch (status) {
    case 'active':
      return <Activity size={12} className="status-icon active" />;
    case 'paused':
      return <Pause size={12} className="status-icon paused" />;
    case 'circuit_breaker':
      return <AlertCircle size={12} className="status-icon circuit-breaker" />;
    case 'stopped':
    default:
      return <Power size={12} className="status-icon stopped" />;
  }
};

const formatStatusText = (status: BotStatus): string => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'paused':
      return 'Paused';
    case 'circuit_breaker':
      return 'Circuit Breaker';
    case 'stopped':
    default:
      return 'Stopped';
  }
};

// ============================================================================
// EXECUTION HISTORY COMPONENT
// ============================================================================

interface ExecutionItemProps {
  execution: AutoBotExecution;
}

const ExecutionItem: React.FC<ExecutionItemProps> = ({ execution }) => {
  const timeAgo = formatTimeAgo(execution.timestamp);
  const isSuccess = execution.status === 'success';
  const isBuy = execution.action === 'Buy';

  return (
    <div className={`execution-item ${isSuccess ? 'success' : 'failed'}`}>
      <div className="execution-icon">
        {isBuy ? (
          <ArrowUpCircle size={14} className="buy" />
        ) : (
          <ArrowDownCircle size={14} className="sell" />
        )}
      </div>
      <div className="execution-info">
        <span className="execution-symbol">{execution.symbol}</span>
        <span className="execution-qty">×{execution.quantity}</span>
        {execution.fillPrice && (
          <span className="execution-price">@{execution.fillPrice}</span>
        )}
      </div>
      <div className="execution-status">
        {isSuccess ? (
          <CheckCircle size={12} className="success-icon" />
        ) : (
          <AlertCircle size={12} className="error-icon" />
        )}
      </div>
      <div className="execution-time">{timeAgo}</div>
    </div>
  );
};

const formatTimeAgo = (timestamp: number): string => {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(timestamp).toLocaleDateString();
};

/**
 * Why each bot did or didn't act.
 *
 * Executions only show what DID happen; almost always the useful question is
 * why nothing did — outside hours, score too low, globally disarmed. Polls the
 * engine, which owns the trail.
 */
interface BotDecision {
  timestamp: number;
  botId: string;
  botName: string;
  symbol: string | null;
  signalType: string;
  confidence: number | null;
  price: number | null;
  outcome: string;
  reason: string;
}


/** One filled order as recorded from the broker's execution report. */
interface FillRow {
  id: string;
  timestamp: number;
  accountId: number;
  symbol: string;
  side: 'Buy' | 'Sell';
  quantity: number;
  price: number;
  status: string;
  orderId: string;
  source?: string;
}

/**
 * A completed round trip, built by walking fills in time order and matching
 * each exit against the open position.
 *
 * Pairing is done here rather than trusted from the broker because Tradovate's
 * realizedPnL is a per-ACCOUNT running total: reading trade P&L out of its
 * deltas attributes every fill on the account — including manual trades — to
 * whatever the bot happened to do last. That is exactly how a night of winning
 * trades came to show a $616 loss.
 */
interface RoundTrip {
  symbol: string;
  qty: number;
  entrySide: 'Buy' | 'Sell';
  entry: number;
  exit: number;
  openedAt: number;
  closedAt: number;
  points: number;
}

function pairFills(fills: FillRow[]): RoundTrip[] {
  const bySymbol = new Map<string, FillRow[]>();
  // The same fill can reach us twice (tradovate-service double-emits, and a
  // reconnect can replay). A duplicate would pair against itself and show a
  // round trip that never happened, so key on the fill id.
  const seen = new Set<string>();
  for (const f of fills) {
    if (f.status !== 'Filled' || !f.price) continue;
    const id = f.id ?? `${f.orderId}:${f.timestamp}:${f.price}`;
    if (seen.has(id)) continue;
    seen.add(id);
    const k = `${f.accountId}:${f.symbol}`;
    if (!bySymbol.has(k)) bySymbol.set(k, []);
    bySymbol.get(k)!.push(f);
  }

  const out: RoundTrip[] = [];
  for (const rows of bySymbol.values()) {
    rows.sort((a, b) => a.timestamp - b.timestamp);
    // FIFO: an opposite-side fill closes the oldest open lot.
    const open: FillRow[] = [];
    for (const f of rows) {
      const opposite = open.length > 0 && open[0].side !== f.side;
      if (!opposite) {
        open.push(f);
        continue;
      }
      const e = open.shift()!;
      const points = e.side === 'Buy' ? f.price - e.price : e.price - f.price;
      out.push({
        symbol: f.symbol,
        qty: Math.min(e.quantity, f.quantity),
        entrySide: e.side,
        entry: e.price,
        exit: f.price,
        openedAt: e.timestamp,
        closedAt: f.timestamp,
        points,
      });
    }
  }
  return out.sort((a, b) => b.closedAt - a.closedAt);
}

const hhmmss = (t: number): string =>
  new Date(t).toLocaleTimeString('en-US', { hour12: false });

/**
 * Fills and round trips, straight from the broker's execution reports.
 *
 * Shows what actually filled and at what price — the thing that was
 * unanswerable from this system until now.
 */
const FillsLog: React.FC<{ expanded: boolean; onToggle: () => void }> = ({
  expanded,
  onToggle,
}) => {
  const [fills, setFills] = useState<FillRow[]>([]);
  const [mode, setMode] = useState<'trips' | 'fills'>('trips');

  useEffect(() => {
    if (!expanded) return;
    let alive = true;
    const load = async (): Promise<void> => {
      try {
        const base = CONFIG.getBackendUrl(window.location.hostname);
        const r = await fetch(`${base}/api/trade-transactions?limit=400`);
        const d = await r.json();
        const rows: FillRow[] = Array.isArray(d) ? d : d.transactions ?? d.data ?? [];
        if (alive) setFills(rows);
      } catch {
        // engine restarting — keep what we have
      }
    };
    load();
    const id = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [expanded]);

  const filled = fills.filter((f) => f.status === 'Filled' && f.price > 0);
  const trips = pairFills(fills);

  return (
    <div className="autobot-decisions">
      <button className="autobot-decisions-header" onClick={onToggle}>
        <span>Fills &amp; trades</span>
        <span className="autobot-decisions-count">
          {expanded ? `${filled.length}` : 'show'}
        </span>
      </button>
      {expanded && (
        <div className="autobot-decisions-list">
          <div className="autobot-fills-tabs">
            <button
              className={mode === 'trips' ? 'active' : ''}
              onClick={() => setMode('trips')}
            >
              round trips ({trips.length})
            </button>
            <button
              className={mode === 'fills' ? 'active' : ''}
              onClick={() => setMode('fills')}
            >
              fills ({filled.length})
            </button>
          </div>

          {mode === 'trips' &&
            (trips.length === 0 ? (
              <div className="autobot-decisions-empty">
                No completed round trips yet.
              </div>
            ) : (
              trips.slice(0, 40).map((t, i) => (
                <div key={`${t.closedAt}-${i}`} className="autobot-fill-row">
                  <span className="af-time">{hhmmss(t.closedAt)}</span>
                  <span className={`af-side ${t.entrySide === 'Buy' ? 'long' : 'short'}`}>
                    {t.entrySide === 'Buy' ? 'LONG' : 'SHORT'}
                  </span>
                  <span className="af-px">
                    {t.entry} &rarr; {t.exit}
                  </span>
                  <span className={`af-pts ${t.points >= 0 ? 'win' : 'loss'}`}>
                    {t.points >= 0 ? '+' : ''}
                    {t.points.toFixed(2)} pt
                  </span>
                  <span className="af-held">
                    {Math.max(1, Math.round((t.closedAt - t.openedAt) / 1000))}s
                  </span>
                </div>
              ))
            ))}

          {mode === 'fills' &&
            (filled.length === 0 ? (
              <div className="autobot-decisions-empty">
                No fills recorded yet. Older rows may predate fill capture.
              </div>
            ) : (
              filled.slice(0, 60).map((f) => (
                <div key={f.id} className="autobot-fill-row">
                  <span className="af-time">{hhmmss(f.timestamp)}</span>
                  <span className={`af-side ${f.side === 'Buy' ? 'long' : 'short'}`}>
                    {f.side.toUpperCase()}
                  </span>
                  <span className="af-px">
                    {f.quantity} {f.symbol} @ {f.price}
                  </span>
                  <span className="af-held">{f.source ?? ''}</span>
                </div>
              ))
            ))}
        </div>
      )}
    </div>
  );
};

const DecisionLog: React.FC<{ expanded: boolean; onToggle: () => void }> = ({
  expanded,
  onToggle,
}) => {
  const [decisions, setDecisions] = useState<BotDecision[]>([]);

  useEffect(() => {
    if (!expanded) return;
    let alive = true;
    const load = async (): Promise<void> => {
      try {
        // /auto-bot/decisions is served at the engine ROOT, not under /api,
        // so this can't reuse getApiBaseUrl() unmodified.
        const base = CONFIG.getBackendUrl(window.location.hostname);
        const r = await fetch(`${base}/auto-bot/decisions?limit=40`);
        const d = await r.json();
        if (alive && Array.isArray(d.decisions)) setDecisions(d.decisions);
      } catch {
        // engine may be restarting — leave the last list in place
      }
    };
    load();
    const id = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [expanded]);

  return (
    <div className="autobot-decisions">
      <button className="autobot-decisions-header" onClick={onToggle}>
        <span>Decision log</span>
        <span className="autobot-decisions-count">
          {expanded ? `${decisions.length}` : 'show'}
        </span>
      </button>
      {expanded && (
        <div className="autobot-decisions-list">
          {decisions.length === 0 ? (
            <div className="autobot-decisions-empty">
              No decisions yet — nothing has reached a bot.
            </div>
          ) : (
            decisions.map((d, i) => (
              <div key={`${d.timestamp}-${i}`} className={`autobot-decision ${d.outcome}`}>
                <span className="ad-time">
                  {new Date(d.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                <span className="ad-sym">{d.symbol?.split('.')[0] ?? '—'}</span>
                {d.confidence !== null && <span className="ad-conf">{d.confidence.toFixed(0)}</span>}
                <span className={`ad-outcome ${d.outcome}`}>{d.outcome}</span>
                <span className="ad-reason">{d.reason}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

interface ExecutionHistoryProps {
  executions: AutoBotExecution[];
  expanded: boolean;
  onToggle: () => void;
}

const ExecutionHistory: React.FC<ExecutionHistoryProps> = ({
  executions,
  expanded,
  onToggle,
}) => {
  return (
    <div className="execution-history">
      <div className="execution-history-header" onClick={onToggle}>
        <div className="execution-history-title">
          <History size={14} />
          <span>Recent Executions</span>
          <span className="execution-count">{executions.length}</span>
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>
      {expanded && (
        <div className="execution-history-list">
          {executions.length === 0 ? (
            <div className="execution-empty">No executions yet</div>
          ) : (
            executions.map((exec, idx) => (
              <ExecutionItem key={`${exec.timestamp}-${idx}`} execution={exec} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// BOT LOGS COMPONENT
// ============================================================================

interface BotLogsProps {
  botId: string;
  executions: AutoBotExecution[];
  expanded: boolean;
  onToggle: () => void;
}

const BotLogs: React.FC<BotLogsProps> = ({ botId, executions, expanded, onToggle }) => {
  return (
    <div className="bot-logs">
      <div className="bot-logs-header" onClick={onToggle}>
        <div className="bot-logs-title">
          <FileText size={12} />
          <span>Activity Log</span>
          <span className="bot-logs-count">{executions.length > 10 ? '10+' : executions.length}</span>
        </div>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </div>
      {expanded && (
        <div className="bot-logs-list">
          {executions.length === 0 ? (
            <div className="bot-logs-empty">No activity yet</div>
          ) : (
            executions.slice(0, 10).map((exec, idx) => {
              const timeAgo = formatTimeAgo(exec.timestamp);
              const statusClass = exec.status === 'success' ? 'success' : exec.status === 'skipped' ? 'skipped' : 'failed';
              const errorText = exec.error ? String(exec.error) : '';
              const shortError = errorText.length > 30 ? errorText.substring(0, 30) + '...' : errorText;

              return (
                <div key={`${exec.id || exec.timestamp}-${idx}`} className={`bot-log-entry ${statusClass}`}>
                  <Circle size={6} className="log-bullet" />
                  <span className="log-time">{timeAgo}</span>
                  <span className="log-action">{exec.action}</span>
                  {/* The side shown is the one SENT. On an inverted target that
                      is the opposite of the signal, so say so — otherwise a
                      Sell against a bullish diamond reads as a bug. */}
                  {exec.inverted && (
                    <span className="log-inverted" title="this target trades the opposite side of the signal">
                      inv
                    </span>
                  )}
                  <span className="log-qty">×{exec.quantity}</span>
                  <span className="log-symbol">{exec.symbol}</span>
                  {exec.status === 'skipped' && errorText && (
                    <span className="log-reason" title={errorText}>({shortError})</span>
                  )}
                  {exec.fillPrice && (
                    <span className="log-price">@{exec.fillPrice}</span>
                  )}
                  {exec.pnl !== undefined && exec.pnl !== null && (
                    <span className={`log-pnl ${exec.pnl >= 0 ? 'positive' : 'negative'}`}>
                      {exec.pnl >= 0 ? '+' : ''}${exec.pnl.toFixed(2)}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// BOT CARD COMPONENT
// ============================================================================

interface BotCardProps {
  bot: AutoBot;
  onEnable: (enabled: boolean) => void;
  onPause: () => void;
  onResume: () => void;
  onDelete: () => void;
  onEdit: () => void;
}

const BotCard: React.FC<BotCardProps> = ({
  bot,
  onEnable,
  onPause,
  onResume,
  onDelete,
  onEdit,
}) => {
  const status = bot.state?.status || 'stopped';
  const dailyStats = bot.state?.dailyStats;
  const isBos = isBreakOfStructureBot(bot);
  // Circuit-breaker "stop_after_loss" halts the bot after a losing trade.
  // Re-arm by toggling enable off/on (backend re-arms on enable).
  const isHalted = status === 'circuit_breaker';
  const allExecutions = useExecutions();
  const botExecutions = useMemo(() => {
    if (!Array.isArray(allExecutions)) return [];
    return allExecutions.filter((e) => e.botId === bot.id);
  }, [allExecutions, bot.id]);
  const [logsExpanded, setLogsExpanded] = useState(false);

  return (
    <div className={`autobot-card ${status}`}>
      <div className="autobot-card-header">
        <div className="autobot-card-title">
          <Bot size={16} style={{ color: getStatusColor(status) }} />
          <span className="autobot-card-name">{bot.name}</span>
        </div>
        <div className="autobot-card-status">
          {getStatusIcon(status)}
          <span>{formatStatusText(status)}</span>
          {isHalted && (
            <span
              className="autobot-halted-badge"
              title="Halted after a loss (circuit breaker). Toggle enable to re-arm."
            >
              HALTED
            </span>
          )}
        </div>
      </div>

      {/* Show pause info when paused or circuit breaker */}
      {(status === 'paused' || status === 'circuit_breaker') && bot.state && (
        <div className="autobot-card-pause-info">
          {bot.state.pauseReason && (
            <span className="pause-reason">{bot.state.pauseReason}</span>
          )}
          {bot.state.resumeAt && (
            <span className="resume-time">
              Resumes: {new Date(bot.state.resumeAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' })} PST
            </span>
          )}
        </div>
      )}

      <div className="autobot-card-info">
        <div className="autobot-card-strategy">
          <Zap size={12} />
          {isBos ? (
            <span>
              BOS/CHoCH · {bot.triggerMode === 'confirmed' ? 'Confirmed' : 'Instant'} · ≥{bot.minGrade}
              {bot.structureTypes.length > 0 && (
                <> · {bot.structureTypes.map((t) => t.toUpperCase()).join('+')}</>
              )}
            </span>
          ) : (
            <span>{bot.strategyName}</span>
          )}
        </div>
        <div className="autobot-card-symbol">
          <span className="symbol-badge">{bot.symbol === '*' ? 'Any' : bot.symbol}</span>
        </div>
      </div>

      {dailyStats && (
        <div className="autobot-card-stats">
          <div className="stat">
            <span className="stat-label">Trades</span>
            <span className="stat-value">{dailyStats.trades}</span>
          </div>
          <div className="stat">
            <span className="stat-label">W/L</span>
            <span className="stat-value">
              {dailyStats.wins}/{dailyStats.losses}
            </span>
          </div>
          <div className="stat">
            <span className="stat-label">P&L</span>
            <span
              className={`stat-value ${dailyStats.netPnL >= 0 ? 'positive' : 'negative'}`}
            >
              ${dailyStats.netPnL.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      <div className="autobot-card-actions">
        <label className="autobot-toggle">
          <input
            type="checkbox"
            checked={bot.enabled}
            onChange={(e) => onEnable(e.target.checked)}
          />
          <span className="toggle-slider" />
        </label>

        {bot.enabled && status === 'active' && (
          <button className="autobot-btn pause" onClick={onPause} title="Pause">
            <Pause size={14} />
          </button>
        )}

        {bot.enabled && (status === 'paused' || status === 'circuit_breaker') && (
          <button className="autobot-btn resume" onClick={onResume} title="Resume">
            <Play size={14} />
          </button>
        )}

        <button className="autobot-btn settings" onClick={onEdit} title="Settings">
          <Settings size={14} />
        </button>

        <button className="autobot-btn delete" onClick={onDelete} title="Delete">
          <Trash2 size={14} />
        </button>
      </div>

      {/* Bot Activity Logs */}
      <BotLogs
        botId={bot.id}
        executions={botExecutions}
        expanded={logsExpanded}
        onToggle={() => setLogsExpanded(!logsExpanded)}
      />
    </div>
  );
};

// ============================================================================
// MAIN PANEL COMPONENT
// ============================================================================

interface AutoBotPanelProps {
  visible: boolean;
  onClose: () => void;
}

export const AutoBotPanel: React.FC<AutoBotPanelProps> = ({ visible, onClose }) => {
  const {
    bots,
    botsLoading,
    botsError,
    fetchBots,
    fetchExecutions,
    createBot,
    updateBot,
    enableBot,
    pauseBot,
    resumeBot,
    deleteBot,
    setGlobalEnabled,
    selectBot,
    selectedBotId,
  } = useAutoBotStore();

  // Use primitive selectors to avoid infinite re-render loops
  const globalEnabled = useAutoBotGlobalEnabled();
  const activeBots = useAutoBotActiveBots();
  const totalBots = useAutoBotTotalBots();

  // Get executions and memoize the slice to avoid creating new arrays
  const allExecutions = useExecutions();
  const [decisionsExpanded, setDecisionsExpanded] = useState(false);

  const recentExecutions = useMemo(() =>
    Array.isArray(allExecutions) ? allExecutions.slice(0, 20) : [],
    [allExecutions]
  );

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [fillsExpanded, setFillsExpanded] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editingBot, setEditingBot] = useState<AutoBot | undefined>(undefined);

  // Initialize store on mount
  useEffect(() => {
    initializeAutoBotStore();
  }, []);

  // Refresh on visibility, then poll the authoritative bot status while the
  // panel stays open. BOS bot state (active/halted/stopped) is updated
  // asynchronously through the engine->tradovate relay, and a single live
  // socket update can be missed (e.g. a quick disable->enable) — leaving the
  // card stuck on a stale 'stopped'. Polling the engine every few seconds
  // guarantees the card converges to the real status.
  useEffect(() => {
    if (!visible) return;
    fetchBots();
    fetchExecutions(undefined, 100); // Fetch all executions (last 100)
    const poll = setInterval(() => {
      fetchBots();
    }, 4000);
    return () => clearInterval(poll);
  }, [visible, fetchBots, fetchExecutions]);

  // Close editor if the bot being edited is deleted
  useEffect(() => {
    if (editingBot && !bots.find(b => b.id === editingBot.id)) {
      setShowEditor(false);
      setEditingBot(undefined);
    }
  }, [bots, editingBot]);

  if (!visible) return null;

  const handleDelete = (botId: string) => {
    if (confirmDelete === botId) {
      deleteBot(botId);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(botId);
      // Reset after 3 seconds
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  const handleCreateBot = () => {
    setEditingBot(undefined);
    setShowEditor(true);
  };

  const handleEditBot = (bot: AutoBot) => {
    setEditingBot(bot);
    setShowEditor(true);
  };

  const handleSaveBot = (botData: DistributiveOmit<AutoBot, 'id' | 'createdAt' | 'updatedAt' | 'state'>) => {
    if (editingBot) {
      // Update existing bot
      updateBot(editingBot.id, botData);
    } else {
      // Create new bot
      createBot(botData);
    }
    setShowEditor(false);
    setEditingBot(undefined);
  };

  const handleCancelEditor = () => {
    setShowEditor(false);
    setEditingBot(undefined);
  };

  return (
    <div className="autobot-overlay" onClick={onClose}>
      <div className="autobot-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="autobot-header">
          <div className="autobot-title">
            <Bot size={20} />
            <span>AUTO-BOT</span>
            <span className="autobot-count">
              {activeBots}/{totalBots}
            </span>
          </div>
          <div className="autobot-header-actions">
            <button
              className="autobot-refresh"
              onClick={fetchBots}
              disabled={botsLoading}
              title="Refresh"
            >
              <RefreshCw size={16} className={botsLoading ? 'spinning' : ''} />
            </button>
            <button className="autobot-close" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Global Toggle */}
        <div className="autobot-global-toggle">
          <div className="global-toggle-info">
            <Power size={16} className={globalEnabled ? 'enabled' : 'disabled'} />
            <span>Global Enable</span>
          </div>
          <label className="autobot-toggle large">
            <input
              type="checkbox"
              checked={globalEnabled}
              onChange={(e) => setGlobalEnabled(e.target.checked)}
            />
            <span className="toggle-slider" />
          </label>
        </div>

        {/* Error Display */}
        {botsError && (
          <div className="autobot-error">
            <AlertCircle size={16} />
            <span>{botsError}</span>
          </div>
        )}

        {/* Execution History */}
        <DecisionLog
          expanded={decisionsExpanded}
          onToggle={() => setDecisionsExpanded(!decisionsExpanded)}
        />

        <FillsLog
          expanded={fillsExpanded}
          onToggle={() => setFillsExpanded(!fillsExpanded)}
        />

        <ExecutionHistory
          executions={recentExecutions}
          expanded={historyExpanded}
          onToggle={() => setHistoryExpanded(!historyExpanded)}
        />

        {/* Bot List */}
        <div className="autobot-list">
          {bots.length === 0 && !botsLoading ? (
            <div className="autobot-empty">
              <Bot size={48} className="empty-icon" />
              <p>No bots configured</p>
              <span>Create your first AUTO-BOT to get started</span>
            </div>
          ) : (
            bots.map((bot) => (
              <BotCard
                key={bot.id}
                bot={bot}
                onEnable={(enabled) => enableBot(bot.id, enabled)}
                onPause={() => pauseBot(bot.id)}
                onResume={() => resumeBot(bot.id)}
                onDelete={() => handleDelete(bot.id)}
                onEdit={() => handleEditBot(bot)}
              />
            ))
          )}
        </div>

        {/* Add Button */}
        <div className="autobot-footer">
          <button className="autobot-add-btn" onClick={handleCreateBot}>
            <Plus size={16} />
            <span>Create Bot</span>
          </button>
        </div>

        {/* Confirm Delete Dialog */}
        {confirmDelete && (
          <div className="autobot-confirm-delete">
            <span>Click delete again to confirm</span>
          </div>
        )}
      </div>

      {/* Bot Editor Modal */}
      {showEditor && (
        <AutoBotEditor
          bot={editingBot}
          onSave={handleSaveBot}
          onCancel={handleCancelEditor}
        />
      )}
    </div>
  );
};

export default AutoBotPanel;
